import { getDateTimeForSorting } from 'shared/utils/dateUtils';

export interface TodoLabelValue {
	name?: string;
	label?: string;
}

export interface TodoPriorityOption {
	id: number;
	label: string;
	name: string;
}

export interface TodoTableRow {
	id: number | string;
	isScheduleEvent?: boolean;
	todoScheduleId?: number;
	description?: string;
	label?: string;
	name?: string;
	title?: string;
	note?: unknown;
	user?: unknown;
	assignedGroup?: unknown;
	assigned?: string;
	category?: unknown;
	priority?: TodoLabelValue | string;
	status?: TodoLabelValue;
	due_date?: string;
	dueDate?: string;
	dueTime?: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	completedAt?: string;
	pastDue?: boolean;
	created_at?: string;
	[key: string]: unknown;
}

const getRecord = (value: unknown): Record<string, unknown> | null =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const asOptionalString = (value: unknown): string | undefined =>
	typeof value === 'string' ? value : undefined;

const getRecordString = (record: Record<string, unknown>, ...keys: string[]): string | undefined => {
	for (const key of keys) {
		const value = asOptionalString(record[key]);
		if (value) return value;
	}
	return undefined;
};

const parseNumericIdFromTail = (raw: string): number | null => {
	const match = raw.match(/(\d+)$/);
	if (match) return Number(match[1]);
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
};

export function getTodoScheduleFromTodo(todo: unknown): Record<string, unknown> | null {
	const record = getRecord(todo);
	if (!record) return null;

	const schedule = record.todoSchedule ?? record.todo_schedule;
	if (schedule == null) return null;

	if (typeof schedule === 'number' && Number.isFinite(schedule)) {
		return { id: schedule };
	}

	if (typeof schedule === 'string') {
		const id = parseNumericIdFromTail(schedule);
		return id != null ? { id } : null;
	}

	return getRecord(schedule);
}

/** @deprecated Use getTodoScheduleFromTodo */
export const getToDoScheduleFromEvent = getTodoScheduleFromTodo;

export function isRecurringTodo(todo: unknown): boolean {
	return getTodoScheduleFromTodo(todo) != null;
}

const capitalizeStatus = (status: string): string =>
	status ? status.charAt(0).toUpperCase() + status.slice(1) : status;

const getLabelValueDisplay = (value: unknown): string | undefined => {
	if (!value) return undefined;
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const display = record.name ?? record.label;
		return typeof display === 'string' ? display : undefined;
	}
	return undefined;
};

export function getTodoPriorityDisplay(
	priority: TodoTableRow['priority'],
	lookup?: ReadonlyMap<number, TodoLabelValue>
): string | undefined {
	return getLabelValueDisplay(resolvePriority(priority, lookup) ?? priority);
}

export function getTodoStatusDisplay(status: TodoTableRow['status']): string | undefined {
	return getLabelValueDisplay(status);
}

export function parsePriorityIdFromRef(value: unknown): number | null {
	if (value == null) return null;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') return parseNumericIdFromTail(value);
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		if (record.id != null) return parsePriorityIdFromRef(record.id);
		if (typeof record['@id'] === 'string') return parseNumericIdFromTail(record['@id']);
	}
	return null;
}

export function normalizePrioritiesList(data: unknown): TodoPriorityOption[] {
	const record = getRecord(data);
	const items = Array.isArray(data)
		? data
		: record
			? asArray(record.member ?? record['hydra:member'] ?? record.items ?? record.data)
			: [];

	return items
		.map((item): TodoPriorityOption | null => {
			const priorityRecord = getRecord(item);
			if (!priorityRecord) return null;
			const id = Number(priorityRecord.id);
			if (!Number.isFinite(id)) return null;
			const label =
				asOptionalString(priorityRecord.label) ?? asOptionalString(priorityRecord.name);
			if (!label) return null;
			return { id, label, name: label };
		})
		.filter((priority): priority is TodoPriorityOption => priority != null);
}

const asArray = (value: unknown): unknown[] => {
	if (Array.isArray(value)) return value;
	const record = getRecord(value);
	if (!record) return [];
	const nested = record.member ?? record['hydra:member'] ?? record.items ?? record.data;
	return Array.isArray(nested) ? nested : [];
};

export function buildPriorityLookup(priorities: TodoPriorityOption[]): Map<number, TodoLabelValue> {
	return new Map(
		priorities.map((priority) => [
			priority.id,
			{ label: priority.label, name: priority.name ?? priority.label },
		])
	);
}

export function getPriorityDisplayValue(
	priority: unknown,
	lookup?: ReadonlyMap<number, TodoLabelValue>
): string | undefined {
	return getLabelValueDisplay(resolvePriority(priority, lookup));
}

const resolvePriority = (
	priority: unknown,
	lookup?: ReadonlyMap<number, TodoLabelValue>
): TodoTableRow['priority'] => {
	if (!priority) return undefined;

	if (typeof priority === 'object') {
		const record = priority as Record<string, unknown>;
		const label = asOptionalString(record.label) ?? asOptionalString(record.name);
		if (label) {
			return { label, name: label };
		}
		const id = parsePriorityIdFromRef(priority);
		if (id != null && lookup?.has(id)) {
			return lookup.get(id);
		}
		return undefined;
	}

	if (typeof priority === 'string') {
		const id = parsePriorityIdFromRef(priority);
		if (id != null && lookup?.has(id)) {
			return lookup.get(id);
		}
	}

	return undefined;
};

const resolveStatus = (status: unknown): TodoTableRow['status'] | undefined => {
	if (!status) return undefined;
	if (typeof status === 'string') {
		return { name: status, label: capitalizeStatus(status) };
	}
	if (typeof status === 'object') {
		const record = status as Record<string, unknown>;
		const name = asOptionalString(record.name) ?? asOptionalString(record.label);
		if (name) {
			return { name, label: capitalizeStatus(name) };
		}
	}
	return undefined;
};

export function normalizeTodoToTableRow(
	todo: unknown,
	priorityLookup?: ReadonlyMap<number, TodoLabelValue>
): TodoTableRow | null {
	const record = getRecord(todo);
	if (!record || record.id == null) return null;

	const schedule = getTodoScheduleFromTodo(todo);
	const isRecurring = schedule != null;
	const status = resolveStatus(record.status);

	return {
		...record,
		id: Number(record.id),
		isScheduleEvent: isRecurring,
		todoScheduleId: schedule?.id != null ? Number(schedule.id) : undefined,
		description: asOptionalString(record.description),
		note: record.note,
		user: record.user,
		assignedGroup: record.assignedGroup ?? record.assigned_group,
		category: record.category,
		priority: resolvePriority(record.priority, priorityLookup),
		status,
		due_date: getRecordString(record, 'due_date', 'dueDate'),
		dueDate: getRecordString(record, 'due_date', 'dueDate'),
		dueTime: getRecordString(record, 'dueTime', 'due_time'),
		completedAt: getRecordString(record, 'completedAt', 'completed_at'),
		pastDue: Boolean(record.pastDue ?? record.past_due ?? record.isPastDueFlag ?? record.is_past_due_flag),
		created_at: asOptionalString(record.created_at),
	};
}

export type TodoSortOrder = 'dueDateAsc' | 'completedDateDesc';

const getNumericId = (item: TodoTableRow): number => Number(item.id) || 0;

const getDueDateTimestamp = (item: TodoTableRow): number => {
	if (item.scheduledDateFormatted) {
		return getDateTimeForSorting(item.scheduledDateFormatted, item.scheduledTimeFormatted).getTime();
	}
	const dueDate = item.due_date ?? item.dueDate;
	return getDateTimeForSorting(dueDate, item.dueTime).getTime();
};

const getCompletedDateTimestamp = (item: TodoTableRow): number => {
	if (!item.completedAt) return 0;
	const parsed = Date.parse(item.completedAt);
	if (!Number.isNaN(parsed)) return parsed;
	return getDateTimeForSorting(item.completedAt).getTime();
};

export function sortTodoRowsByDueDateAsc(items: TodoTableRow[]): TodoTableRow[] {
	return [...items].sort((a, b) => {
		const timeA = getDueDateTimestamp(a);
		const timeB = getDueDateTimestamp(b);
		const missingA = timeA === 0;
		const missingB = timeB === 0;
		if (missingA && !missingB) return 1;
		if (!missingA && missingB) return -1;
		const timeDiff = timeA - timeB;
		if (timeDiff !== 0) return timeDiff;
		return getNumericId(a) - getNumericId(b);
	});
}

export function sortTodoRowsByCompletedDateDesc(items: TodoTableRow[]): TodoTableRow[] {
	return [...items].sort((a, b) => {
		const timeDiff = getCompletedDateTimestamp(b) - getCompletedDateTimestamp(a);
		if (timeDiff !== 0) return timeDiff;
		return getNumericId(b) - getNumericId(a);
	});
}

export function normalizeAndSortTodos(
	todos: unknown[],
	priorityLookup?: ReadonlyMap<number, TodoLabelValue>,
	sortOrder: TodoSortOrder = 'dueDateAsc'
): TodoTableRow[] {
	const rows = todos
		.map((todo) => normalizeTodoToTableRow(todo, priorityLookup))
		.filter((row): row is TodoTableRow => row != null);
	return sortOrder === 'completedDateDesc'
		? sortTodoRowsByCompletedDateDesc(rows)
		: sortTodoRowsByDueDateAsc(rows);
}
